'use client';

import { useMemo, useState, useEffect } from 'react';
import * as React from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { PlanCard } from './plan-card';
import { AddOnCard } from './addon-card';
import { PricingSummary } from './pricing-summary';

interface PricingItem {
    translationKey: string;
    price: number;
    categoryKey: string;
}

interface PricingData {
    plans: PricingItem[];
    addOns: PricingItem[];
}

interface PricingCalculatorProps {
    pricingData: PricingData;
    formData: {
        name: string;
        email: string;
        country: string;
    };
    onExportPDF: (selectedPlans: PricingItem[], selectedAddOns: PricingItem[], totalPrice: number) => void;
    error: string | null;
    message: string | null;
    onClearMessage: () => void;
}

export function PricingCalculator({ pricingData, formData, onExportPDF, error: externalError, message, onClearMessage }: PricingCalculatorProps) {
    const t = useTranslations('pricing');
    const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
    const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(externalError);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                onClearMessage();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message, onClearMessage]);

    // Get selected items
    const selectedPlanItems = useMemo(() => {
        return pricingData.plans.filter((plan) => selectedPlans.includes(plan.translationKey));
    }, [pricingData.plans, selectedPlans]);

    const selectedAddOnItems = useMemo(() => {
        return pricingData.addOns.filter((addon) => selectedAddOns.includes(addon.translationKey));
    }, [pricingData.addOns, selectedAddOns]);

    // Calculate total
    const totalPrice = useMemo(() => {
        const plansPrice = selectedPlanItems.reduce((sum, item) => sum + item.price, 0);
        const addOnsPrice = selectedAddOnItems.reduce((sum, item) => sum + item.price, 0);
        return plansPrice + addOnsPrice;
    }, [selectedPlanItems, selectedAddOnItems]);

    // Handle plan selection (max 2 plans)
    const handlePlanToggle = (planKey: string) => {
        if (selectedPlans.includes(planKey)) {
            // Remove plan
            setSelectedPlans((prev) => prev.filter((key) => key !== planKey));
            setError(null);
        } else {
            // Add plan (max 2)
            if (selectedPlans.length >= 2) {
                setError(t('calculator.errors.maxPlans'));
                setTimeout(() => setError(null), 5000);
            } else {
                setSelectedPlans((prev) => [...prev, planKey]);
                setError(null);
            }
        }
    };

    // Handle add-on selection (multiple select)
    const handleAddOnToggle = (addOnKey: string) => {
        // Check if at least one plan is selected
        if (selectedPlans.length === 0) {
            setError(t('calculator.errors.selectPlanFirst'));
            setTimeout(() => setError(null), 5000);
            return;
        }

        setSelectedAddOns((prev) => {
            if (prev.includes(addOnKey)) {
                return prev.filter((key) => key !== addOnKey);
            } else {
                return [...prev, addOnKey];
            }
        });
        setError(null);
    };

    const handleExportPDF = async () => {

        try {
            // Get translated names for PDF
            const plansWithNames = selectedPlanItems.map(plan => {
                try {
                    // Strip 'pricing.' prefix if present since we're already using 'pricing' namespace
                    const cleanKey = plan.translationKey.startsWith('pricing.') 
                        ? plan.translationKey.replace(/^pricing\./, '') 
                        : plan.translationKey;
                    const planName = t(`${cleanKey}.name`) || plan.translationKey;
                    return { ...plan, name: planName };
                } catch {
                    return { ...plan, name: plan.translationKey };
                }
            });

            const addOnsWithNames = selectedAddOnItems.map(addon => {
                try {
                    // Strip 'pricing.' prefix if present since we're already using 'pricing' namespace
                    const cleanKey = addon.translationKey.startsWith('pricing.') 
                        ? addon.translationKey.replace(/^pricing\./, '') 
                        : addon.translationKey;
                    const addonName = t(`${cleanKey}.name`) || addon.translationKey;
                    return { ...addon, name: addonName };
                } catch {
                    return { ...addon, name: addon.translationKey };
                }
            });

            await onExportPDF(plansWithNames, addOnsWithNames, totalPrice);
        } catch (error) {

            setError(t('calculator.errors.pdfFailed'));
        }
    };

    // Update error when external error changes
    useEffect(() => {
        setError(externalError);
    }, [externalError]);

    return (
        <SectionWrapper variant="default" padding="md">
            <div className="mx-auto max-w-7xl space-y-6">
                {/* Page Title */}
                <SectionHeading
                    page="pricing-calculator"
                    fallbackPill={t('calculator.pill')}
                    fallbackTitle={t('calculator.title')}
                    fallbackSubtitle={t('calculator.description')}
                    align="center"
                    maxWidthClass="max-w-3xl"
                    titleVariant="h2"
                />

                {/* Informational Banner */}
                <div className="bg-yellow-50 dark:bg-yellow-900/60 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <p className="text-md text-yellow-800 dark:text-yellow-200">
                        {t('calculator.infoBanner', { phone: t('calculator.phone') })}
                    </p>
                </div>

                {/* Error Message */}
                {(error || message) && (
                    <div className={`rounded-lg border px-4 py-3 text-sm ${error
                        ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200'
                        : 'border-green-200 bg-green-50 text-green-800 dark:border-green-500/60 dark:bg-green-500/10 dark:text-green-200'
                        }`}>
                        <div className="flex items-start gap-2">
                            {error ? <AlertCircle className="mt-0.5 h-5 w-5" /> : <CheckCircle className="mt-0.5 h-5 w-5" />}
                            <span>{error || message}</span>
                        </div>
                    </div>
                )}

                {/* Three Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left Column - Select MLM Plans */}
                    <div className="lg:col-span-4">
                        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                {t('plans.selectTitle')}
                            </h3>
                            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                                {pricingData.plans.map((plan) => (
                                    <PlanCard
                                        key={plan.translationKey}
                                        translationKey={plan.translationKey}
                                        price={plan.price}
                                        isSelected={selectedPlans.includes(plan.translationKey)}
                                        onToggle={() => handlePlanToggle(plan.translationKey)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Middle Column - Select Add-ons and Design Customization */}
                    <div className="lg:col-span-4">
                        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                {t('addons.selectTitle')}
                            </h3>
                            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                                {pricingData.addOns.map((addon) => (
                                    <AddOnCard
                                        key={addon.translationKey}
                                        translationKey={addon.translationKey}
                                        price={addon.price}
                                        categoryKey={addon.categoryKey}
                                        isSelected={selectedAddOns.includes(addon.translationKey)}
                                        onToggle={() => handleAddOnToggle(addon.translationKey)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Selected Plan & Add On Summary */}
                    <div className="lg:col-span-4">
                        <PricingSummary
                            selectedPlans={selectedPlanItems}
                            selectedAddOns={selectedAddOnItems}
                            totalPrice={totalPrice}
                            onExportPDF={handleExportPDF}
                        />
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

